import type { BackendClient } from '../bridge/backend-client';
import type { ControllerManager } from './controller-manager';
import type { CameraManager } from './camera-manager';
import type { ModelManager } from './model-manager';
import type { IntegrationManager } from './integration-manager';
import type { DashboardManager } from './dashboard-manager';
import type { PluginTypesManager } from './plugin-types-manager';

export type ConnIntent = 'connected' | 'disconnected';

/*
 * ConnectionManager — the FRONTEND's INTENT to be attached to a gateway, held on
 * the GatewayEntry beside the data managers (so home + detail read `intent` and
 * call connect()/disconnect() without prop-drilling, exactly like the other
 * managers). This is NOT the gateway's own up/down state — that's the
 * ControllerManager's data-plane liveness + leaf presence (see conn-state.ts).
 *
 * Disconnect is purely OUR-frontend-side: it stops THIS gateway's data streams
 * (controllers/models/pluginTypes — their ports close, the worker tears the
 * sources down) and pauses its LAN/cloud race probes (setGatewayActive(false)).
 * The box itself is untouched — it stays connected to the cloud, keeps running,
 * keeps mirroring. Connect re-opens the streams (fresh snapshots) and resumes the
 * race. Cancel (mid-connecting) is the same teardown as disconnect.
 */
export class ConnectionManager extends EventTarget {
  private desired: ConnIntent = 'connected';

  constructor(
    private readonly backend: BackendClient,
    private readonly gatewayId: string,
    private readonly deps: {
      controllers: ControllerManager;
      cameras: CameraManager;
      models: ModelManager;
      integrations: IntegrationManager;
      // Held here since the badge started counting it: a Disconnect that left this
      // one stream running kept a subscription open that nothing on screen owned.
      dashboards: DashboardManager;
      pluginTypes: PluginTypesManager;
    },
  ) {
    super();
  }

  /** Whether the user wants this gateway connected. Default 'connected'. */
  get intent(): ConnIntent {
    return this.desired;
  }

  /** Detach our frontend from the gateway: tear down its data streams + pause its
   *  network race. Frontend-only — the box stays cloud-connected. Also the Cancel
   *  action while a connect is still establishing. */
  disconnect(): void {
    if (this.desired === 'disconnected') return;
    this.desired = 'disconnected';
    this.deps.controllers.stop();
    this.deps.cameras.stop();
    this.deps.models.stop();
    this.deps.integrations.stop();
    this.deps.dashboards.stop();
    this.deps.pluginTypes.stop();
    void this.backend.api.setGatewayActive(this.gatewayId, false);
    this.dispatchEvent(new CustomEvent('change'));
  }

  /** Re-attach: resume the LAN/cloud race + re-open the data streams. The
   *  ControllerManager re-subscribes fresh ('connecting' until its snapshot), so
   *  the badge honestly walks connecting → connected. */
  connect(): void {
    if (this.desired === 'connected') return;
    this.desired = 'connected';
    void this.backend.api.setGatewayActive(this.gatewayId, true);
    void this.deps.controllers.start();
    void this.deps.cameras.start();
    void this.deps.models.start();
    void this.deps.integrations.start();
    void this.deps.dashboards.start();
    void this.deps.pluginTypes.start();
    this.dispatchEvent(new CustomEvent('change'));
  }
}
